import { useEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { NotificationView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import {
  ACKNOWLEDGE_COPY,
  ALERT_COPY,
  canAcknowledge,
  notificationReason,
  notificationSentence,
} from '../copy';
import { useMarkRead } from '../hooks/useMarkRead';
import { useOpenNotification } from '../hooks/useOpenNotification';
import { MAX_VISIBLE_ALERTS, useAlertStore, useInboxStore } from './store';

/** How long a non-Critical alert stays, not counting time hovered or focused. */
export const ALERT_LIFETIME_MS = 8000;

/**
 * The alerts on screen (spec 136 US1), bottom-right, newest on top. A
 * Critical one stays until someone acts on it; the others leave on their own,
 * pausing while the reader hovers or tabs into them. Never more than three:
 * the rest wait behind "+n more", which opens the inbox, where every one of
 * them is anyway.
 */
export function AlertStack() {
  const alerts = useAlertStore((state) => state.alerts);
  const openInbox = useInboxStore((state) => state.setOpen);
  if (alerts.length === 0) return null;
  const visible = alerts.slice(-MAX_VISIBLE_ALERTS).reverse();
  const hidden = alerts.length - visible.length;
  return (
    <section
      aria-label="Alerts"
      className="fixed right-4 bottom-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {visible.map((notification) => (
        <AlertCard key={notification.id} notification={notification} />
      ))}
      {hidden > 0 ? (
        <Button
          variant="outline"
          size="sm"
          className="self-end"
          onClick={() => {
            openInbox(true);
          }}
        >
          {ALERT_COPY.more(hidden)}
        </Button>
      ) : null}
    </section>
  );
}

function AlertCard({ notification }: { notification: NotificationView }) {
  const dismiss = useAlertStore((state) => state.dismiss);
  const open = useOpenNotification();
  const mark = useMarkRead();
  const critical = notification.severity === 'critical';
  const sentence = notificationSentence(notification);
  const reason = notificationReason(notification);
  // Hovering or focusing the alert stops its clock; leaving restarts it with
  // what was left, not afresh.
  const [paused, setPaused] = useState(false);
  const left = useRef(ALERT_LIFETIME_MS);

  useEffect(() => {
    if (critical || paused) return undefined;
    const started = Date.now();
    const timer = setTimeout(() => {
      dismiss(notification.id);
    }, left.current);
    return () => {
      clearTimeout(timer);
      left.current = Math.max(0, left.current - (Date.now() - started));
    };
  }, [critical, paused, dismiss, notification.id]);

  return (
    <div
      // Critical interrupts a screen reader; everything else waits its turn.
      role={critical ? 'alert' : 'status'}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      onFocus={() => {
        setPaused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false);
        }
      }}
      className={cn(
        'bg-popover text-popover-foreground shadow-floating flex flex-col gap-1.5 rounded-lg p-3 ring-1',
        critical ? 'ring-destructive/60' : 'ring-foreground/10',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {critical ? (
            <span className="text-destructive text-2xs font-medium tracking-wide uppercase">
              {ALERT_COPY.critical}
            </span>
          ) : null}
          <p className="text-foreground text-sm">{sentence}</p>
          <p className="text-muted-foreground text-xs">
            {notification.workspace.name ?? 'Workspace'}
            {reason ? ` · ${reason}` : ''}
          </p>
        </div>
        <button
          type="button"
          aria-label={`${ALERT_COPY.dismiss}: ${sentence}`}
          onClick={() => {
            dismiss(notification.id);
          }}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-1 rounded-sm p-1 focus-visible:ring-3 focus-visible:outline-none"
        >
          <XIcon aria-hidden="true" className="size-3.5" />
        </button>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={critical ? 'default' : 'outline'}
          onClick={() => {
            dismiss(notification.id);
            open(notification);
          }}
        >
          {ALERT_COPY.open}
        </Button>
        {canAcknowledge(notification) ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              dismiss(notification.id);
              mark.mutate({
                kind: 'acknowledge',
                notificationId: notification.id,
              });
            }}
          >
            {ACKNOWLEDGE_COPY.action}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
